import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { Room, RoomEvent, Track, type RemoteTrack } from 'livekit-client';
import { uiSocket } from './socket';
import { useLive } from './store';
import type { UiMessage } from '../api/types';

/**
 * LiveKit media (PROTOCOL §8/§11). One Room per watched robot.
 * Components call `useWatch(serial, visible, source)`; the manager ref-counts per robot and source and sends
 * `{type:"media", serial, action:"watch", sources:["camera"|"screen",…]}` / `unwatch` on /ws/ui (§15).
 * The server answers with a viewer token. The robot screen arrives as a second video track (source screen_share).
 */

export type VideoSource = 'camera' | 'screen';

export type MediaConnState = 'requesting' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface MediaInfo {
  state: MediaConnState;
  error?: string;
  hasVideo: boolean;
  hasScreen: boolean;
  hasAudio: boolean;
  viewers: number;
  talking: boolean;
  canPlayAudio: boolean;
  /** Identity of the current tracks (`<room epoch>:<track sid>`); hooks re-attach only when these change. */
  videoKey?: string;
  screenKey?: string;
  audioKey?: string;
  /** start_failed: the server retries at this time (ms epoch). */
  retryAt?: number;
  /** A subscribed video track that decodes no frames (e.g. static robot screen, missed key frame). */
  stalled?: { camera?: boolean; screen?: boolean };
  rev: number;
}

interface MediaStore {
  notConfigured: boolean;
  byRobot: Record<string, MediaInfo>;
  tileSound: Record<string, boolean>;
  toggleTileSound: (serial: string) => void;
}

export const useMedia = create<MediaStore>((set) => ({
  notConfigured: false,
  byRobot: {},
  tileSound: {},
  toggleTileSound: (serial) => set((s) => ({ tileSound: { ...s.tileSound, [serial]: !s.tileSound[serial] } })),
}));

interface Entry {
  room: Room | null;
  video: RemoteTrack | null;
  screen: RemoteTrack | null;
  audio: RemoteTrack | null;
  /** Sources last requested from the server (sorted, comma-joined). */
  sources: string;
  epoch: number;
}

const entries = new Map<string, Entry>();
const wantCounts = new Map<string, { camera: number; screen: number }>();
let epochSeq = 1;
/** Re-subscribe attempts per robot+source (reset when the source set is re-requested). */
const stallTries = new Map<string, number>();
let syncTimer: ReturnType<typeof setTimeout> | null = null;

function patch(serial: string, p: Partial<MediaInfo> | null) {
  useMedia.setState((s) => {
    const byRobot = { ...s.byRobot };
    if (p === null) delete byRobot[serial];
    else {
      const cur = byRobot[serial] ?? { state: 'requesting', hasVideo: false, hasScreen: false, hasAudio: false, viewers: 0, talking: false, canPlayAudio: true, rev: 0 };
      byRobot[serial] = { ...cur, ...p, rev: cur.rev + 1 };
    }
    return { byRobot };
  });
}

/** serial → requested sources ("camera", "camera,screen", "screen"). */
function wanted(): Map<string, string> {
  const out = new Map<string, string>();
  if (document.hidden || useMedia.getState().notConfigured) return out;
  const robots = useLive.getState().robots;
  for (const [serial, n] of wantCounts) {
    if (!robots[serial]?.online) continue;
    const src = [n.camera > 0 && 'camera', n.screen > 0 && 'screen'].filter(Boolean).join(',');
    if (src) out.set(serial, src);
  }
  return out;
}

function sync() {
  syncTimer = null;
  if (!uiSocket.open) return;
  const want = wanted();
  for (const serial of [...entries.keys()]) if (!want.has(serial)) close(serial, true);
  for (const [serial, sources] of want) {
    const e = entries.get(serial);
    if (e) {
      if (e.sources !== sources) {
        stallTries.delete(`${serial}:camera`);
        stallTries.delete(`${serial}:screen`);
        // Source set changed (e.g. Camera ↔ Screen switch): re-send watch; the room stays connected.
        e.sources = sources;
        uiSocket.send({ type: 'media', serial, action: 'watch', sources: sources.split(',') });
      }
      continue;
    }
    entries.set(serial, { room: null, video: null, screen: null, audio: null, sources, epoch: epochSeq++ });
    patch(serial, { state: 'requesting', error: undefined });
    uiSocket.send({ type: 'media', serial, action: 'watch', sources: sources.split(',') });
  }
}

export function scheduleSync(delay = 200) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(sync, delay);
}

function close(serial: string, sendUnwatch: boolean) {
  const e = entries.get(serial);
  if (!e) return;
  entries.delete(serial);
  if (sendUnwatch) uiSocket.send({ type: 'media', serial, action: 'unwatch' });
  void e.room?.disconnect();
  patch(serial, null);
}

function closeAll(sendUnwatch: boolean) {
  for (const serial of [...entries.keys()]) close(serial, sendUnwatch);
}

function countViewers(room: Room): number {
  let n = 1;
  for (const p of room.remoteParticipants.values()) if (p.identity.startsWith('ui-')) n++;
  return n;
}

async function onMediaMessage(msg: Extract<UiMessage, { type: 'media' }>) {
  if (msg.serial === '*' && msg.error === 'socket_closed') {
    closeAll(false); // server unwatches on socket close
    return;
  }
  if (msg.error === 'not_configured') {
    useMedia.setState({ notConfigured: true });
    closeAll(false);
    return;
  }
  const e = entries.get(msg.serial);
  if (!e) return;
  if (msg.error) {
    // start_failed {retryInS}: the server retries mediaStart itself and sends the token when it works.
    const retryAt = typeof msg.retryInS === 'number' && msg.retryInS > 0 ? Date.now() + msg.retryInS * 1000 : undefined;
    if (e.room && msg.error === 'update_failed') patch(msg.serial, { error: msg.message || msg.error });
    else patch(msg.serial, { state: 'error', error: msg.message || msg.error, retryAt });
    return;
  }
  if (e.room || !msg.url || !msg.token) return;
  const serial = msg.serial;
  // No adaptiveStream: robots publish a single layer, and bandwidth is controlled by watch/unwatch of visible robots.
  // (adaptiveStream pauses tracks based on element visibility, which broke re-attaching after remounts.)
  const room = new Room({ adaptiveStream: false, dynacast: false });
  e.room = room;
  patch(serial, { state: 'connecting', retryAt: undefined, error: undefined });
  const refresh = () => {
    if (entries.get(serial) !== e) return;
    const key = (t: RemoteTrack | null) => (t ? `${e.epoch}:${t.sid ?? t.mediaStreamTrack?.id ?? '?'}` : undefined);
    patch(serial, {
      videoKey: key(e.video),
      screenKey: key(e.screen),
      audioKey: key(e.audio),
      hasVideo: Boolean(e.video), hasScreen: Boolean(e.screen), hasAudio: Boolean(e.audio), viewers: countViewers(room), canPlayAudio: room.canPlaybackAudio });
  };
  room
    .on(RoomEvent.TrackSubscribed, (track) => {
      if (track.kind === Track.Kind.Video) {
        const kind = track.source === Track.Source.ScreenShare ? 'screen' : 'camera';
        if (kind === 'screen') e.screen = track;
        else e.video = track;
        const tries = (stallTries.get(`${serial}:${kind}`) ?? 0) + 1;
        stallTries.set(`${serial}:${kind}`, tries);
        watchStall(serial, e, room, track, kind, tries <= 2);
      }
      if (track.kind === Track.Kind.Audio) e.audio = track;
      refresh();
    })
    .on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach();
      if (e.video === track) e.video = null;
      if (e.screen === track) e.screen = null;
      if (e.audio === track) e.audio = null;
      refresh();
    })
    .on(RoomEvent.ParticipantConnected, refresh)
    .on(RoomEvent.ParticipantDisconnected, refresh)
    .on(RoomEvent.AudioPlaybackStatusChanged, refresh)
    .on(RoomEvent.Disconnected, () => {
      if (entries.get(serial) !== e) return;
      e.video = e.screen = e.audio = null;
      patch(serial, { state: 'disconnected', hasVideo: false, hasScreen: false, hasAudio: false, talking: false, videoKey: undefined, screenKey: undefined, audioKey: undefined });
    });
  try {
    await room.connect(msg.url, msg.token);
    if (entries.get(serial) !== e) {
      void room.disconnect();
      return;
    }
    patch(serial, { state: 'connected' });
    refresh();
  } catch (err) {
    patch(serial, { state: 'error', error: `LiveKit: ${(err as Error).message}` });
  }
}

/** Dev-only introspection (window.__cruzr.media()). */
export function debugMedia() {
  return [...entries].map(([serial, e]) => ({
    serial,
    sources: e.sources,
    video: e.video ? { sid: e.video.sid, source: e.video.source, w: e.video.mediaStreamTrack?.getSettings().width } : null,
    screen: e.screen
      ? {
          sid: e.screen.sid,
          source: e.screen.source,
          w: e.screen.mediaStreamTrack?.getSettings().width,
          ready: e.screen.mediaStreamTrack?.readyState,
          mstMuted: e.screen.mediaStreamTrack?.muted,
          attached: e.screen.attachedElements.map((el) => ({ w: (el as HTMLVideoElement).videoWidth, paused: (el as HTMLVideoElement).paused, vis: getComputedStyle(el).visibility, sz: `${el.clientWidth}x${el.clientHeight}` })),
        }
      : null,
    pubs: e.room
      ? [...e.room.remoteParticipants.values()].flatMap((p) =>
          [...p.trackPublications.values()].map((t) => ({ who: p.identity, source: t.source, kind: t.kind, subscribed: t.isSubscribed, enabled: t.isEnabled, muted: t.isMuted, dims: t.dimensions })),
        )
      : null,
  }));
}

/** Dev-only: inbound RTP stats of a robot's video tracks. */
export async function debugStats(serial: string) {
  const e = entries.get(serial);
  const one = async (t: RemoteTrack | null) => {
    if (!t) return null;
    const rep = await (t as unknown as { getRTCStatsReport?: () => Promise<RTCStatsReport | undefined> }).getRTCStatsReport?.();
    const out: Record<string, unknown>[] = [];
    rep?.forEach((v: Record<string, unknown>) => {
      if (v.type === 'inbound-rtp') out.push({ bytes: v.bytesReceived, pkts: v.packetsReceived, dec: v.framesDecoded, recv: v.framesReceived, kf: v.keyFramesDecoded, pli: v.pliCount, nack: v.nackCount, w: v.frameWidth });
    });
    return { sid: t.sid, out };
  };
  return { camera: await one(e?.video ?? null), screen: await one(e?.screen ?? null) };
}

/** Frames decoded so far on a remote video track (null if stats unavailable). */
async function framesDecoded(t: RemoteTrack): Promise<number | null> {
  const rep = await (t as unknown as { getRTCStatsReport?: () => Promise<RTCStatsReport | undefined> }).getRTCStatsReport?.().catch(() => undefined);
  if (!rep) return null;
  let n: number | null = null;
  rep.forEach((v: Record<string, unknown>) => {
    if (v.type === 'inbound-rtp' && typeof v.framesDecoded === 'number') n = (n ?? 0) + v.framesDecoded;
  });
  return n;
}

/**
 * Stall watchdog for a freshly subscribed video track: if no frame is decoded after 5 s, re-subscribe once
 * (fresh subscription → the SFU asks the robot for a key frame); if still nothing after 5 more seconds,
 * mark it stalled so the UI can explain the black picture.
 */
function watchStall(serial: string, e: Entry, room: Room, track: RemoteTrack, kind: 'camera' | 'screen', resubscribe: boolean) {
  const current = () => entries.get(serial) === e && (kind === 'screen' ? e.screen : e.video) === track;
  const mark = (v: boolean) => {
    const cur = useMedia.getState().byRobot[serial]?.stalled ?? {};
    if (Boolean(cur[kind]) !== v) patch(serial, { stalled: { ...cur, [kind]: v } });
  };
  mark(false);
  setTimeout(async () => {
    if (!current() || ((await framesDecoded(track)) ?? 0) !== 0) return; // no inbound-rtp report yet = no frames
    const pub = resubscribe ? [...room.remoteParticipants.values()].flatMap((p) => [...p.trackPublications.values()]).find((p) => p.track === track) : undefined;
    if (pub) {
      pub.setSubscribed(false);
      setTimeout(() => pub.setSubscribed(true), 300);
      return; // TrackSubscribed fires again with the new track → a new watchdog
    }
    mark(true);
    // Recover as soon as frames start flowing (e.g. the robot screen changes).
    const id = setInterval(async () => {
      if (!current()) return clearInterval(id);
      if (((await framesDecoded(track)) ?? 0) > 0) {
        mark(false);
        clearInterval(id);
      }
    }, 2000);
  }, 5000);
}

let installed = false;
export function installMedia() {
  if (installed) return;
  installed = true;
  uiSocket.on((m) => {
    if (m.type === 'media') void onMediaMessage(m);
    if (m.type === 'robots' || m.type === 'robotStatus') scheduleSync(50);
  });
  uiSocket.onOpen(() => scheduleSync(0));
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      closeAll(true);
    } else scheduleSync(0);
  });
  window.addEventListener('beforeunload', () => closeAll(true));
}

export function resetMedia() {
  closeAll(false);
  useMedia.setState({ notConfigured: false, byRobot: {} });
}

/** Keep a robot's stream (camera or screen) open while `active` (component visible). */
export function useWatch(serial: string | undefined, active = true, source: VideoSource = 'camera') {
  useEffect(() => {
    if (!serial || !active) return;
    const c = wantCounts.get(serial) ?? { camera: 0, screen: 0 };
    c[source]++;
    wantCounts.set(serial, c);
    scheduleSync(0);
    return () => {
      const n = wantCounts.get(serial);
      if (n) {
        n[source] = Math.max(0, n[source] - 1);
        if (!n.camera && !n.screen) wantCounts.delete(serial);
      }
      scheduleSync(400); // small grace period so grid → detail doesn't churn
    };
  }, [serial, active, source]);
}

// DOM helpers (kept outside the hooks: the elements live in React state).
function clearSrc(el: HTMLMediaElement) {
  el.srcObject = null;
}
function setLevel(el: HTMLMediaElement, volume: number, muted: boolean) {
  el.volume = Math.max(0, Math.min(1, volume));
  el.muted = muted;
}

/**
 * Attach the robot's camera (or screen) track to a <video> element. Returns a callback ref.
 * Each element attaches/detaches only itself; re-attaches when the element, the source or the track changes
 * (a track published into an already-connected room, a Camera↔Screen switch, a remount).
 */
export function useVideoTrack(serial: string | undefined, source: VideoSource = 'camera') {
  const [el, setEl] = useState<HTMLVideoElement | null>(null);
  const key = useMedia((s) => (serial ? (source === 'screen' ? s.byRobot[serial]?.screenKey : s.byRobot[serial]?.videoKey) : undefined));
  useEffect(() => {
    if (!el || !serial) return;
    const e = entries.get(serial);
    const track = source === 'screen' ? e?.screen : e?.video;
    if (!key || !track) {
      clearSrc(el);
      return;
    }
    track.attach(el);
    void el.play().catch(() => undefined);
    return () => {
      track.detach(el);
    };
  }, [el, serial, source, key]);
  return setEl;
}

/** Attach the robot's audio track to an <audio> element when `enabled`. Returns a callback ref. */
export function useAudioTrack(serial: string | undefined, enabled: boolean, volume = 1, muted = false) {
  const [el, setEl] = useState<HTMLAudioElement | null>(null);
  const key = useMedia((s) => (serial ? s.byRobot[serial]?.audioKey : undefined));
  useEffect(() => {
    if (!el || !serial) return;
    const track = entries.get(serial)?.audio;
    if (!enabled || !key || !track) {
      clearSrc(el);
      return;
    }
    track.attach(el);
    void el.play().catch(() => undefined);
    return () => {
      track.detach(el);
    };
  }, [el, serial, key, enabled]);
  useEffect(() => {
    if (!el) return;
    setLevel(el, volume, muted);
  }, [el, volume, muted, key]);
  return setEl;
}

export async function startAudio(serial: string) {
  await entries.get(serial)?.room?.startAudio().catch(() => undefined);
  const e = entries.get(serial);
  if (e?.room) patch(serial, { canPlayAudio: e.room.canPlaybackAudio });
}

/** Publish (or stop) the browser microphone into one robot's room. Returns false if not connected. */
export async function setTalk(serial: string, on: boolean): Promise<boolean> {
  const e = entries.get(serial);
  if (!e?.room || useMedia.getState().byRobot[serial]?.state !== 'connected') return false;
  await e.room.startAudio().catch(() => undefined);
  await e.room.localParticipant.setMicrophoneEnabled(on, { echoCancellation: true, noiseSuppression: true });
  patch(serial, { talking: on });
  return true;
}

export function stopAllTalk() {
  for (const [serial, info] of Object.entries(useMedia.getState().byRobot)) if (info.talking) void setTalk(serial, false).catch(() => undefined);
}

/** i18n key (or raw error text) describing why there is no picture; null when video is playing. */
export function mediaLabel(info: MediaInfo | undefined, notConfigured: boolean, online: boolean, source: VideoSource = 'camera', screenStatus?: unknown): string | null {
  if (notConfigured) return 'media.notConfigured';
  if (!online) return 'media.offline';
  if (source === 'screen' && screenStatus === 'needs_consent') return 'media.needsConsent';
  if (!info) return 'media.idle';
  if (info.state === 'error') return info.error ?? 'media.error';
  if (source === 'screen') return info.hasScreen ? (info.stalled?.screen ? 'media.noFrames' : null) : info.state === 'connected' ? 'media.waitingScreen' : `media.${info.state}`;
  if (info.hasVideo) return info.stalled?.camera ? 'media.noFrames' : null;
  return `media.${info.state}`;
}
